import { ImSchool } from 'Src/ng2/shared/services/im-models/im-school';
import { CurrentSchoolYear } from '../../../ng2/shared/constants/current-school-year.constant';
import { BackgroundJobSpinner } from './../../shared/modals/background-job/background-job-spinner.service';

import * as angular from 'angular';
import { IController, ITimeoutService } from 'angular';
import * as _ from 'lodash';
import * as moment from 'moment';

import { Store } from '@ngrx/store';
import { UtilitiesService } from 'src/ng2/shared/services/utilities/utilities.service';
import { CREDIT_REQS } from '../../../ng2/shared/constants/credit-reqs.constant';
import { ChangeSavedInMetrics } from '../../../ng2/store/actions/metrics-actions';
import { SdcActionsService } from './../../sdc/flux/sdc-actions.service';
import { ISdcStore } from './../../sdc/flux/sdc-store.interface';
import { SpinnerModal } from './../../shared/modals/status-spinner/status-spinner.service';

export class CreditMetricsCtrl implements IController {
  static $inject = [
    '$mdDialog',
    '$scope',
    '$timeout',
    'BackgroundJobSpinner',
    'ErrorModal',
    'ImSchool',
    'Rollbar',
    'SDCStore',
    'SpinnerModal',
    'Store',
    'UtilitiesService',
    'backgroundJob',
    'sdcActions',
  ];

  // Bindings
  private changeMadeInMetrics;
  private lastSaveTimeStamp;
  private cohortsByTerm;
  private cohortsFollowSameSequence;
  private term;
  private confirm;
  private creditTableCopy;
  private upcomingGradDates;
  private nextFourGradYears;
  private currentSchool: any;

  constructor(
    private $mdDialog,
    private $scope,
    private $timeout: ITimeoutService,
    private backgroundJobSpinner: BackgroundJobSpinner,
    private ErrorModal,
    private ImSchool: ImSchool,
    private Rollbar,
    private SDCStore: ISdcStore,
    private SpinnerModal: SpinnerModal,
    private store: Store<any>,
    private UtilitiesService: UtilitiesService,
    private backgroundJob,
    private sdcActions: SdcActionsService,
  ) {}

  $onInit() {
    this.SpinnerModal.deactivateSpinner();
    this.changeMadeInMetrics = { status: false };
    this.lastSaveTimeStamp = '';

    // this is a pointer to the ontrackMetrics.creditsExpectedByTerm;
    this.cohortsByTerm = this.currentSchool.onTrackMetrics.creditsExpectedByTerm;

    this.cohortsFollowSameSequence = this.cohortsByTerm.allCohorts !== null ? true : false;
    this.term = this.currentSchool.termStructure;

    this.creditTableCopy = this.UtilitiesService.copyPOJO(this.cohortsByTerm);

    this.upcomingGradDates = this.ImSchool.getUpcomingGradDates();
    this.nextFourGradYears = _.uniq(_.map(this.upcomingGradDates, ({ year }) => `cohort_${year}`));

    // sdcAction to store:
    const updateSchool = () => {
      const storeSchool = this.SDCStore.getSchool();
      const storeSchoolCopy = this.UtilitiesService.copyPOJO(storeSchool);
      this.currentSchool = _.merge(this.currentSchool, storeSchoolCopy);
    };
    this.$scope.$listenTo(this.SDCStore, ['school'], updateSchool);
  }

  public dialogConfirmation(ev): void {
    this.confirm = this.$mdDialog.confirm({
      title: 'Modify tables?',
      textContent: 'All unsaved changes will be lost.',
      ariaLabel: 'Warning',
      targetEvent: ev,
      ok: 'Continue',
      cancel: 'Cancel',
    });

    this.$mdDialog
      .show(this.confirm)
      .then(() => {
        if (this.cohortsFollowSameSequence) this._bundleCohorts();
        else this._unbundleCohorts();
        this.creditTableCopy = this.UtilitiesService.copyPOJO(this.cohortsByTerm);
      })
      .catch(err => {
        this.cohortsFollowSameSequence = !this.cohortsFollowSameSequence;
      });
  }

  // Validate Table : SaveBtn.disabled ? true : false
  public validateTable() {
    // If no changes have made, return true (e.g. disabled);
    if (!this.changeMadeInMetrics.status) return true;

    let validationErrors = 0;
    const trimmedCohortByTerm = _(this.cohortsByTerm)
      .omitBy(_.isNull)
      .value();
    const allKeys = Object.keys(trimmedCohortByTerm);
    const firstKey = allKeys[0];
    const allCreditReqObjs = trimmedCohortByTerm[firstKey];
    const creditReqObj = allCreditReqObjs[0];
    const creditReqKeys: string[] = _.keys(creditReqObj);

    // Schools have metric data for previous cohorts, need to ignore
    const upcomingGradDates = this.ImSchool.getUpcomingGradDates();
    const upcomingCohortKeys = upcomingGradDates.map(({ year }) => `cohort_${year}`);
    upcomingCohortKeys.push('allCohorts');

    _.forIn(trimmedCohortByTerm, (cohortTable, cohortKey) => {
      const isNextFour = _.includes(upcomingCohortKeys, cohortKey);
      if (!isNextFour) return;

      for (let creditReqKey of creditReqKeys) {
        const creditReq: any = _.get(CREDIT_REQS, creditReqKey);
        const gradReq = creditReq && creditReq.gradReq;

        const rowValues = cohortTable.map(cohortRowData => cohortRowData[creditReqKey]);

        for (let i = 0; i < rowValues.length; i++) {
          const nextIsGreater = rowValues[i] > rowValues[i + 1];
          const endTermDoesNotMeetReq = rowValues[rowValues.length - 1] < gradReq;
          if (nextIsGreater) {
            validationErrors++;
          } else if (endTermDoesNotMeetReq) {
            validationErrors++;
          }
        }
      }
    });
    return validationErrors;
  }

  public save(): void {
    const path = `onTrackMetrics.creditsExpectedByTerm`;
    // patch needed to update store
    const patch = this.currentSchool.generatePatch([path]);
    this.currentSchool
      .$patchFields([path], (cs, headers) => {
        // bgjId will be "null" if no BGJ was enqueued; Do nothing.
        const bgjId = headers('nv-background-jobs');
        if (bgjId === 'null') return Promise.resolve(null);

        const job = this.backgroundJob(bgjId);
        const backgroundJobPromise = job.promise();
        this.backgroundJobSpinner.activateSpinner('Updating Students', backgroundJobPromise, job);

        return backgroundJobPromise
          .then(
            () => {
              this.lastSaveTimeStamp = 'Last saved: ' + moment().format('MM/DD/YY \\at hh:mmA');
              this.changeMadeInMetrics.status = false;
              this.store.dispatch(new ChangeSavedInMetrics());
              this.creditTableCopy = this.UtilitiesService.copyPOJO(this.cohortsByTerm);
              this.sdcActions.updateSchoolByPatch(this.UtilitiesService.copyPOJO(patch));
            },
            error => {
              this.ErrorModal.show('An error occurred while updating students', error);
            },
            notification => {
              // tslint:disable-next-line:no-console
              if (notification) console.log(notification);
            },
          )
          .catch(err => {
            this.Rollbar.error(err);
          });
      })
      .catch(function(errorResponse) {
        this.ErrorModal.show('Unable to save your changes', errorResponse);
      });
  }

  private _unbundleCohorts(): void {
    let allCohorts = this.cohortsByTerm.allCohorts;
    let cohortKeys = _.keys(this.cohortsByTerm);
    let newCohortsByTerm: any = {};
    _.pull(cohortKeys, 'allCohorts');
    // want to limit the keys here to prevent overwriting cohort history
    _.each(cohortKeys, key => {
      if (_.includes(this.nextFourGradYears, key)) newCohortsByTerm[key] = angular.copy(allCohorts);
    });
    newCohortsByTerm.allCohorts = null;
    this.currentSchool.onTrackMetrics.creditsExpectedByTerm = newCohortsByTerm;
    this.cohortsByTerm = newCohortsByTerm;
  }

  private _bundleCohorts(): void {
    const cohortKeys = _.keys(this.cohortsByTerm);
    // remove allCohorts:
    _.pull(cohortKeys, 'allCohorts');
    // sort:
    cohortKeys.sort((a, b) => parseInt(a) - parseInt(b));
    // update allCohorts prop with next grad cohort data:
    let newCohortsByTerm = {
      allCohorts: this.UtilitiesService.copyPOJO(this.cohortsByTerm[`cohort_${CurrentSchoolYear.ENDFULL}`]),
    };
    // want to limit the keys here to prevent overwriting cohort history
    _.each(cohortKeys, key => {
      if (_.includes(this.nextFourGradYears, key)) newCohortsByTerm[key] = null;
    });
    // reset currentSchool onTrackMetrics & cohortsByTerm
    this.currentSchool.onTrackMetrics.creditsExpectedByTerm = newCohortsByTerm;
    this.cohortsByTerm = newCohortsByTerm;
  }
}

angular.module('nv.SchoolMetricsModule').controller('CreditMetricsCtrl', CreditMetricsCtrl);
